import { Grid, Typography } from '@material-ui/core';
import { FormControl, FormControlLabel, FormLabel } from '@mui/material';
import { AxiosResponse } from 'axios';
import _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLayoutContext } from '../../../Context/Layout.context';
import { getPasswordPolicy, putPasswordPolicy } from '../../../services/users/users.service';
import { CancelButton } from '../../buttons/CancelButton.component';
import { ConfirmButton } from '../../buttons/ConfirmButton.component';
import { CheckboxOne } from '../../CheckboxOne/CheckboxOne.component';
import { LeavePrompt } from '../../Prompt/LeavePrompt/LeavePrompt.component';
import { Prompt } from '../../Prompt/Prompt.component';
import { TextFieldOne } from '../../TextFieldOne/TextFieldOne.component';
import { IPasswordPolicy } from '../../UserProfile/IUserProfile.types';
import { useStyles } from '../SystemSettings.styles';
import SwitchOne from '../../SwitchOne/SwitchOne.component';

export const PasswordPolicyManager: React.FC<{
  onClose: () => void;
  onChange: (isChanged?: boolean) => void;
}> = ({ onClose, onChange }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [passwordPolicy, setPasswordPolicy] = useState<IPasswordPolicy>({} as IPasswordPolicy);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isLeavePromptOpen, setIsLeavePromptOpen] = useState<boolean>(false);
  const [apiResponse, setApiResponse] = useState<string>('');
  const { openAlert, showLoader } = useLayoutContext();
  const isPasswordLenghtWrong: boolean = passwordPolicy.passwordLength < 10 || passwordPolicy.passwordLength > 24;
  const isAuthCodeExpirationWrong: boolean =
    passwordPolicy.twoFactorAuth &&
    (passwordPolicy.authCodeExpirationSeconds < 60 || passwordPolicy.authCodeExpirationSeconds > 600);
  const isPasswordExpirationWrong: boolean =
    passwordPolicy.passwordExpirationDays < 0 || passwordPolicy.passwordExpirationDays > 365;
  const [isDataChanged, setIsDataChanged] = useState<boolean>(false);

  const handleOnSave = () => {
    setIsModalOpen(true);
  };

  const handleOnClose = () => {
    isDataChanged ? setIsLeavePromptOpen(true) : onClose();
  };

  useEffect(() => {
    getPasswordPolicy()
      .then((response: AxiosResponse<IPasswordPolicy>) => {
        setPasswordPolicy(response.data);
        setApiResponse(JSON.stringify(response.data));
      })
      .catch(() => console.log('error'));
  }, []);

  const saveChanges = () => {
    showLoader(true);
    putPasswordPolicy(passwordPolicy)
      .then((response: AxiosResponse) => {
        response.status === 200 || response.status === 204 ? openAlert('success') : openAlert('save_error');
        setApiResponse(JSON.stringify(passwordPolicy));
        setIsDataChanged(false);
      })
      .catch(() => {
        openAlert('save_error');
      })
      .finally(() => {
        setIsModalOpen(false);
        showLoader(false);
      });
  };

  const handleValueCallback = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target && e.target?.name) {
        const key = e.target.name;
        if (
          e.target.name.includes('required') ||
          e.target.name.includes('password') ||
          e.target.name.includes('authCode')
        ) {
          setPasswordPolicy((prev) => ({
            ...prev,
            [key]: e.target.value !== '' ? parseFloat(e.target.value).toFixed(0) : null,
          }));
        } else {
          setPasswordPolicy((prev) =>
          {
            const isTwoFactorAuthChangedToTrue = key === 'twoFactorAuth' && e.target.checked === true;
            return ({
              ...prev,
              [key]: e.target.checked, ...(isTwoFactorAuthChangedToTrue && { authCodeExpirationSeconds: 120 })
            })
          });
        }
      }
    },
    [passwordPolicy, setPasswordPolicy],
  );

  useEffect(() => {
    if (apiResponse !== '' && passwordPolicy) {
      setIsDataChanged(JSON.stringify(passwordPolicy) !== apiResponse);
    }
  }, [passwordPolicy, apiResponse]);

  useEffect(() => {
    onChange(isDataChanged);
  }, [isDataChanged]);

  const {
    passwordLength,
    requireLowercase,
    requireUppercase,
    passwordExpirationDays,
    requireNonAlphanumeric,
    requireDigit,
    twoFactorAuth,
    authCodeExpirationSeconds,
  } = passwordPolicy;

  return (
    <>
      <Grid container>
        <Grid item xs={12}>
          <div className={classes.listHeader}>
            <Typography className={classes.tabTitle}>{t('systemSettings:passwordPolicy.header')}</Typography>
          </div>
        </Grid>
        <Grid item xs={12}>
          <FormControl
            focused={false}
            sx={{
              marginTop: -1,
              '& .MuiFormLabel-root': {
                fontWeight: 700,
                fontSize: 14,
              },
            }}
            component="fieldset"
            variant="standard"
          >
            <FormLabel sx={{ color: '#2C2D3E', margin: '10px 0px 10px 0px' }} component="legend">
              Długość hasła (wymagane)
            </FormLabel>
            <TextFieldOne
              area-label="Wartość"
              name="passwordLength"
              required
              data-testid="passwordLength"
              label="Wartość"
              type="number"
              onChange={handleValueCallback}
              value={passwordLength ?? ''}
              error={isPasswordLenghtWrong}
              helperText={isPasswordLenghtWrong ? 'Podana wartość musi zawierać się w zakresie liczb 10-24' : undefined}
              inputProps={{
                step: '1',
              }}
              sx={{
                width: 120,
                fontSize: 10,
                marginTop: 1,
                '& .MuiFormHelperText-root': {
                  width: 380,
                  marginLeft: -0.3,
                },
              }}
            />
            <FormLabel component="legend" sx={{ color: '#2C2D3E', margin: '10px 0px 10px 0px' }}>
              Stopień skomplikowania hasła
            </FormLabel>

            <FormControlLabel
              control={
                <CheckboxOne
                  name={'requireUppercase'}
                  onChange={handleValueCallback}
                  checked={requireUppercase ?? false}
                  value={'requireUppercase'}
                />
              }
              label="Hasło musi zawierać przynajmniej 1 wielką literę"
            />
            <FormControlLabel
              control={
                <CheckboxOne
                  name={'requireLowercase'}
                  onChange={handleValueCallback}
                  checked={requireLowercase ?? false}
                />
              }
              label="Hasło musi zawierać przynajmniej 1 małą literę"
            />
            <FormControlLabel
              control={
                <CheckboxOne name={'requireDigit'} onChange={handleValueCallback} checked={requireDigit ?? false} />
              }
              label="Hasło musi zawierać przynajmniej 1 cyfrę"
            />
            <FormControlLabel
              control={
                <CheckboxOne
                  name="requireNonAlphanumeric"
                  onChange={handleValueCallback}
                  checked={requireNonAlphanumeric ?? false}
                />
              }
              label={`Hasło musi zawierać przynajmniej 1 znak specjalny:  ! " # $ % & ' ( ) * + , - . / : ; < = > ? @ [ \ ] ^ _\` { | }`}
            />

            <FormLabel sx={{ color: '#2C2D3E', margin: '10px 0px 10px 0px' }} component="legend">
              Liczba dni ważności hasła (wymagane)
            </FormLabel>
            <TextFieldOne
              area-label="Wartość"
              required
              name="passwordExpirationDays"
              data-testid="passwordExpirationDays"
              label="Wartość"
              type="number"
              onChange={handleValueCallback}
              inputProps={{
                step: '1',
              }}
              sx={{
                width: 120,
                fontSize: 10,
                marginTop: 1,
                '& .MuiFormHelperText-root': {
                  marginLeft: -0.3,
                  width: 380,
                },
              }}
              error={isPasswordExpirationWrong}
              value={passwordExpirationDays ?? ''}
              helperText={
                isPasswordExpirationWrong ? 'Podana wartość musi zawierać się w zakresie liczb 0-365' : undefined
              }
            />
            <div style={{ display: 'flex', justifyContent: 'left', marginTop: '10px' }}>
              <FormLabel sx={{ color: '#2C2D3E', fontSize: '14px', fontWeight: 'bold', margin: '7px 0px 10px 0px', }}>Dwuetapowa autoryzacja</FormLabel>
              <SwitchOne
                name={'twoFactorAuth'}
                disableRipple
                color="primary"
                checked={twoFactorAuth ?? false}
                onChange={handleValueCallback}
                inputProps={{ 'aria-label': 'Dwuetapowa autoryzacja' }}
              />
            </div>
            {twoFactorAuth && (<>
              <FormLabel sx={{ color: '#2C2D3E', margin: '10px 0px 10px 0px' }} component="legend">
                Liczba sekund ważności kodu autoryzacyjnego
              </FormLabel>
              <TextFieldOne
                area-label="Wartość"
                required
                name="authCodeExpirationSeconds"
                data-testid="authCodeExpirationSeconds"
                label="Wartość"
                type="number"
                onChange={handleValueCallback}
                inputProps={{
                  step: '1',
                }}
                sx={{
                  width: 120,
                  fontSize: 10,
                  marginTop: 1,
                  '& .MuiFormHelperText-root': {
                    marginLeft: -0.3,
                    width: 380,
                  },
                }}
                error={isAuthCodeExpirationWrong}
                value={authCodeExpirationSeconds ?? ''}
                helperText={
                  isAuthCodeExpirationWrong ? 'Podana wartość musi zawierać się w zakresie liczb 60-600' : undefined
                }
              />
            </>)}
          </FormControl>
        </Grid>
        <Grid
          item
          xs={12}
          style={{
            textAlign: 'right',
            alignItems: 'end',
            marginTop: isPasswordExpirationWrong || isPasswordLenghtWrong ? 0 : 24,
          }}
        >
          <CancelButton onClick={handleOnClose}>Anuluj</CancelButton>
          <ConfirmButton
            onClick={handleOnSave}
            disabled={
              !isDataChanged ||
              isPasswordLenghtWrong ||
              isPasswordExpirationWrong ||
              !passwordExpirationDays ||
              !passwordLength ||
              isAuthCodeExpirationWrong
            }
          >
            Zapisz
          </ConfirmButton>
        </Grid>
      </Grid>
      <Prompt
        type="confirmation"
        isBold={true}
        size={'xs'}
        open={isModalOpen}
        mainText={`Czy na pewno chcesz zmienić politykę bezpieczeństwa hasła dla wszystkich użytkowników ${t(
          'global:shortNames.system',
        )}?`}
        mainActionButtonText="Tak"
        onConfirm={saveChanges}
        onCancel={() => setIsModalOpen(false)}
      />
      <LeavePrompt isOpen={isLeavePromptOpen} onConfirm={onClose} onCancel={() => setIsLeavePromptOpen(false)} />
    </>
  );
};
